/**
 * Gmail Signature Studio — /crm/signature-studio (hidden route, no sidebar entry).
 *
 * Users design a Gmail-safe email signature from structured blocks; the page
 * owns the signature spec (single source of truth), undo/redo history,
 * Firestore autosave (users/{uid}/gmailSignatures), the uploaded-image
 * library, and the FutureBot registration so the bot can build/edit the
 * signature live while the user watches the preview update.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  FiChevronDown,
  FiCornerUpLeft,
  FiCornerUpRight,
  FiDownloadCloud,
  FiEdit3,
  FiPlus,
  FiTrash2,
} from 'react-icons/fi';
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useAgentPage } from '../../../components/FutureBot/FutureBotProvider';
import {
  blankSpec,
  buildTemplateSpec,
  sanitizeSpec,
  TEMPLATES,
  updateGlobalStyle,
  renameSignature,
  addRow,
  removeRow,
  updateColumn,
  addBlock,
  updateBlock,
  removeBlock,
  moveBlock,
  createBlock,
} from './signatureSpec';
import { getSignatureStats } from './compileSignatureHtml';
import BlockEditorPanel from './BlockEditorPanel';
import SignaturePreview from './SignaturePreview';
import InstallPanel from './InstallPanel';
import ImageLibrary from './ImageLibrary';
import ImportSignatureModal from './ImportSignatureModal';
import useSignatureStudioAgentTools from '../agent/useSignatureStudioAgentTools';

const MAX_HISTORY = 50;
const GLOW_DURATION_MS = 2600;
const SIGNATURES_COLLECTION = 'gmailSignatures';
const LIBRARY_DOC_ID = 'imageLibrary';

function newSignatureDocId() {
  return `sig_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function SignatureStudioPage() {
  const { currentUser, users, NMLS } = useAuth();
  const toast = useToast();

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const panelBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const [loading, setLoading] = useState(true);
  const [signatures, setSignatures] = useState([]); // [{ id, name, updatedAt }]
  const [activeSigId, setActiveSigId] = useState(null);
  const [spec, setSpec] = useState(null);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [agentTouchedIds, setAgentTouchedIds] = useState(() => new Set());
  const [saveState, setSaveState] = useState('saved'); // saved | saving
  const templatePicker = useDisclosure();
  const importModal = useDisclosure();

  const uid = currentUser?.uid;

  // ---------------------------------------------------------------------
  // Profile (prefills templates; refreshed every render via ref for tools)
  // ---------------------------------------------------------------------
  const profile = useMemo(() => {
    const me = (users || []).find((u) => u.value === uid) || {};
    return {
      name: me.label || currentUser?.displayName || '',
      jobTitle: me.jobTitle || 'Loan Officer',
      nmls: NMLS || me.NMLS || '',
      phone: me.phoneNo || '',
      email: currentUser?.email || '',
      website: me.website || '',
      photoUrl: me.photoURL || currentUser?.photoURL || '',
      logoUrl: '',
    };
  }, [users, uid, currentUser, NMLS]);

  // Fresh-state refs so stable tool closures never read stale data.
  const specRef = useRef(spec);
  specRef.current = spec;
  const imagesRef = useRef(images);
  imagesRef.current = images;
  const profileRef = useRef(profile);
  profileRef.current = profile;
  const historyRef = useRef(history);
  historyRef.current = history;
  const signaturesRef = useRef(signatures);
  signaturesRef.current = signatures;

  // ---------------------------------------------------------------------
  // Load signatures + image library
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!uid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'users', uid, SIGNATURES_COLLECTION));
        const sigs = [];
        let library = null;
        snap.forEach((d) => {
          const data = d.data();
          if (d.id === LIBRARY_DOC_ID || data.kind === 'library') {
            library = data;
          } else {
            sigs.push({ id: d.id, name: data.name || 'My Signature', spec: data.spec, updatedAtMs: data.updatedAtMs || 0 });
          }
        });
        if (cancelled) return;
        if (library?.images) setImages(library.images);
        if (sigs.length > 0) {
          sigs.sort((a, b) => (b.updatedAtMs || 0) - (a.updatedAtMs || 0));
          setSignatures(sigs.map(({ id, name, updatedAtMs }) => ({ id, name, updatedAtMs })));
          const first = sigs[0];
          const sanitized = sanitizeSpec(first.spec || {});
          setActiveSigId(first.id);
          setSpec(sanitized.ok ? { ...sanitized.spec, name: first.name } : blankSpec(profileRef.current));
        } else {
          const p = profileRef.current;
          const initial = p.photoUrl
            ? buildTemplateSpec('classic-headshot', p).spec
            : blankSpec(p);
          initial.name = 'My Signature';
          const id = newSignatureDocId();
          setActiveSigId(id);
          setSpec(initial);
          setSignatures([{ id, name: initial.name, updatedAtMs: Date.now() }]);
        }
      } catch (err) {
        console.error('[SignatureStudio] load failed:', err);
        if (!cancelled) {
          setSpec(blankSpec(profileRef.current));
          setActiveSigId(newSignatureDocId());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // ---------------------------------------------------------------------
  // Autosave (debounced)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (loading || !uid || !activeSigId || !spec) return undefined;
    setSaveState('saving');
    const t = setTimeout(async () => {
      try {
        await setDoc(
          doc(db, 'users', uid, SIGNATURES_COLLECTION, activeSigId),
          {
            kind: 'signature',
            name: spec.name || 'My Signature',
            spec,
            updatedAt: serverTimestamp(),
            updatedAtMs: Date.now(),
          },
          { merge: true }
        );
        setSaveState('saved');
        setSignatures((prev) => prev.map((s) => (s.id === activeSigId ? { ...s, name: spec.name || s.name } : s)));
      } catch (err) {
        console.error('[SignatureStudio] autosave failed:', err);
        setSaveState('saved');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [spec, uid, activeSigId, loading]);

  const persistImages = useCallback(async (nextImages) => {
    setImages(nextImages);
    if (!uid) return;
    try {
      await setDoc(
        doc(db, 'users', uid, SIGNATURES_COLLECTION, LIBRARY_DOC_ID),
        { kind: 'library', images: nextImages, updatedAt: serverTimestamp() },
        { merge: true }
      );
    } catch (err) {
      console.error('[SignatureStudio] image library save failed:', err);
    }
  }, [uid]);

  // ---------------------------------------------------------------------
  // Spec mutations + history
  // ---------------------------------------------------------------------
  const glowBlocks = useCallback((ids) => {
    const idList = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
    if (!idList.length) return;
    setAgentTouchedIds((prev) => {
      const next = new Set(prev);
      idList.forEach((id) => next.add(id));
      return next;
    });
    setTimeout(() => {
      setAgentTouchedIds((prev) => {
        const next = new Set(prev);
        idList.forEach((id) => next.delete(id));
        return next;
      });
    }, GLOW_DURATION_MS);
  }, []);

  const applySpec = useCallback((nextSpec) => {
    setHistory((h) => {
      const cur = specRef.current;
      return cur ? [...h.slice(-(MAX_HISTORY - 1)), cur] : h;
    });
    setFuture([]);
    setSpec(nextSpec);
  }, []);

  /** Run a signatureSpec mutation helper result; glow + history on success. */
  const applyMutation = useCallback((result, touchedIds) => {
    if (!result.ok) return result;
    applySpec(result.spec);
    glowBlocks(touchedIds || result.blockId);
    return result;
  }, [applySpec, glowBlocks]);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.length) return false;
    const prev = h[h.length - 1];
    setHistory(h.slice(0, -1));
    setFuture((f) => [...f, specRef.current]);
    setSpec(prev);
    return true;
  }, []);

  const redo = useCallback(() => {
    setFuture((f) => {
      if (!f.length) return f;
      const next = f[f.length - 1];
      setHistory((h) => [...h.slice(-(MAX_HISTORY - 1)), specRef.current]);
      setSpec(next);
      return f.slice(0, -1);
    });
  }, []);

  const applyTemplate = useCallback((templateId) => {
    const result = buildTemplateSpec(templateId, profileRef.current);
    if (!result.ok) return result;
    const next = result.spec;
    next.name = specRef.current?.name || next.name;
    applySpec(next);
    glowBlocks(next.rows.flatMap((r) => r.columns.flatMap((c) => c.blocks.map((b) => b.id))));
    return { ok: true, spec: next };
  }, [applySpec, glowBlocks]);

  const replaceSpec = useCallback((rawSpec) => {
    const result = sanitizeSpec(rawSpec);
    if (!result.ok) return result;
    const next = result.spec;
    next.name = rawSpec?.name || specRef.current?.name || next.name;
    applySpec(next);
    glowBlocks(next.rows.flatMap((r) => r.columns.flatMap((c) => c.blocks.map((b) => b.id))));
    return { ok: true, spec: next };
  }, [applySpec, glowBlocks]);

  // ---------------------------------------------------------------------
  // Signature management (switch / create / delete / rename)
  // ---------------------------------------------------------------------
  const switchSignature = useCallback(async (sigId) => {
    if (sigId === activeSigId || !uid) return;
    try {
      const snap = await getDocs(collection(db, 'users', uid, SIGNATURES_COLLECTION));
      let target = null;
      snap.forEach((d) => { if (d.id === sigId) target = d.data(); });
      if (!target) return;
      const sanitized = sanitizeSpec(target.spec || {});
      setActiveSigId(sigId);
      setSpec(sanitized.ok ? { ...sanitized.spec, name: target.name || 'My Signature' } : blankSpec(profileRef.current));
      setHistory([]);
      setFuture([]);
      setSelectedBlockId(null);
    } catch (err) {
      toast({ title: 'Could not load that signature', description: err.message, status: 'error', duration: 4000 });
    }
  }, [activeSigId, uid, toast]);

  const createSignature = useCallback((templateId) => {
    const result = buildTemplateSpec(templateId, profileRef.current);
    if (!result.ok) return;
    const next = result.spec;
    const id = newSignatureDocId();
    setActiveSigId(id);
    setSpec(next);
    setHistory([]);
    setFuture([]);
    setSelectedBlockId(null);
    setSignatures((prev) => [{ id, name: next.name, updatedAtMs: Date.now() }, ...prev]);
    templatePicker.onClose();
  }, [templatePicker]);

  /**
   * Create a brand-new signature from an already-imported spec (used by the
   * import modal and the FutureBot import tool). Never touches the signature
   * the user was editing.
   */
  const importAsNewSignature = useCallback((rawSpec, warnings = []) => {
    const result = sanitizeSpec(rawSpec);
    if (!result.ok) return result;
    const next = result.spec;
    next.name = rawSpec?.name || 'Imported from Gmail';
    const id = newSignatureDocId();
    setActiveSigId(id);
    setSpec(next);
    setHistory([]);
    setFuture([]);
    setSelectedBlockId(null);
    setSignatures((prev) => [{ id, name: next.name, updatedAtMs: Date.now() }, ...prev]);
    glowBlocks(next.rows.flatMap((r) => r.columns.flatMap((c) => c.blocks.map((b) => b.id))));
    toast({
      title: 'Signature imported',
      description: warnings.length
        ? `Imported with ${warnings.length} note${warnings.length > 1 ? 's' : ''} — compare against the preview.`
        : 'Compare the preview against your Gmail — it should look identical.',
      status: 'success',
      duration: 5000,
    });
    return { ok: true, spec: next };
  }, [glowBlocks, toast]);

  const deleteSignature = useCallback(async () => {
    if (!uid || !activeSigId) return;
    if (!window.confirm(`Delete signature "${spec?.name}"? This cannot be undone.`)) return;
    try {
      await deleteDoc(doc(db, 'users', uid, SIGNATURES_COLLECTION, activeSigId));
      const remaining = signaturesRef.current.filter((s) => s.id !== activeSigId);
      setSignatures(remaining);
      if (remaining.length) {
        switchSignature(remaining[0].id);
      } else {
        const initial = blankSpec(profileRef.current);
        const id = newSignatureDocId();
        setActiveSigId(id);
        setSpec(initial);
        setSignatures([{ id, name: initial.name, updatedAtMs: Date.now() }]);
        setHistory([]);
        setFuture([]);
      }
    } catch (err) {
      toast({ title: 'Delete failed', description: err.message, status: 'error', duration: 4000 });
    }
  }, [uid, activeSigId, spec, switchSignature, toast]);

  // ---------------------------------------------------------------------
  // FutureBot registration — studioApi read through a ref by stable tools
  // ---------------------------------------------------------------------
  const studioApiRef = useRef({});
  studioApiRef.current = {
    getSpec: () => specRef.current,
    getStats: () => (specRef.current ? getSignatureStats(specRef.current) : null),
    getImages: () => imagesRef.current,
    getProfile: () => profileRef.current,
    getSignatures: () => signaturesRef.current,
    getActiveSigId: () => activeSigId,
    canUndo: () => historyRef.current.length > 0,
    applyMutation,
    applyTemplate,
    replaceSpec,
    importAsNewSignature,
    undo,
    selectBlock: setSelectedBlockId,
  };

  const { registration } = useSignatureStudioAgentTools(studioApiRef, { enabled: !loading });
  useAgentPage(registration);

  // ---------------------------------------------------------------------
  // Manual-editor handlers (BlockEditorPanel)
  // ---------------------------------------------------------------------
  const handleUpdateGlobal = useCallback((patch) => {
    const result = applyMutation(updateGlobalStyle(specRef.current, patch));
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleUpdateBlock = useCallback((blockId, patch) => {
    const result = applyMutation(updateBlock(specRef.current, blockId, patch), []);
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleAddBlock = useCallback((columnId, type, overrides = {}) => {
    const result = applyMutation(addBlock(specRef.current, { columnId, block: createBlock(type, overrides) }));
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
    else setSelectedBlockId(result.blockId);
  }, [applyMutation, toast]);

  const handleRemoveBlock = useCallback((blockId) => {
    const result = applyMutation(removeBlock(specRef.current, blockId), []);
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
    else if (selectedBlockId === blockId) setSelectedBlockId(null);
  }, [applyMutation, selectedBlockId, toast]);

  const handleMoveBlock = useCallback((args) => {
    const result = applyMutation(moveBlock(specRef.current, args), [args.blockId]);
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleUpdateColumn = useCallback((columnId, patch) => {
    const result = applyMutation(updateColumn(specRef.current, columnId, patch), []);
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleAddRow = useCallback((args) => {
    const result = applyMutation(addRow(specRef.current, args || {}));
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleRemoveRow = useCallback((rowId) => {
    const result = applyMutation(removeRow(specRef.current, rowId), []);
    if (!result.ok) toast({ title: result.error, status: 'warning', duration: 3000 });
  }, [applyMutation, toast]);

  const handleRename = useCallback((name) => {
    const result = renameSignature(specRef.current, name);
    if (result.ok) setSpec(result.spec);
  }, []);

  const stats = useMemo(() => (spec ? getSignatureStats(spec) : null), [spec]);

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------
  if (loading || !spec) {
    return (
      <Flex minH="60vh" align="center" justify="center" bg={bgColor}>
        <Spinner size="lg" color="purple.500" />
      </Flex>
    );
  }

  return (
    <Box bg={bgColor} minH="100vh" pb={12}>
      {/* Header */}
      <Box bg={headerBG} borderBottomWidth="1px" borderColor={borderColor} px={{ base: 3, md: 6 }} py={3} position="sticky" top={0} zIndex={5}>
        <Flex align="center" wrap="wrap">
          <HStack spacing={3} flex={1} minW={0}>
            <FiEdit3 size={20} />
            <Heading size="md" whiteSpace="nowrap">Signature Studio</Heading>
            <Badge colorScheme="purple">Beta</Badge>
            <Menu>
              <MenuButton as={Button} size="sm" variant="outline" rightIcon={<FiChevronDown />} maxW="220px">
                <Text isTruncated>{spec.name || 'My Signature'}</Text>
              </MenuButton>
              <MenuList zIndex={20}>
                {signatures.map((s) => (
                  <MenuItem key={s.id} onClick={() => switchSignature(s.id)} fontWeight={s.id === activeSigId ? 'bold' : 'normal'}>
                    {s.name}
                  </MenuItem>
                ))}
                <MenuDivider />
                <MenuItem icon={<FiPlus />} onClick={templatePicker.onOpen}>New signature…</MenuItem>
                <MenuItem icon={<FiDownloadCloud />} onClick={importModal.onOpen}>Import from Gmail…</MenuItem>
                <MenuItem icon={<FiTrash2 />} color="red.400" onClick={deleteSignature}>Delete current</MenuItem>
              </MenuList>
            </Menu>
            <Input
              size="sm"
              variant="flushed"
              value={spec.name || ''}
              onChange={(e) => handleRename(e.target.value)}
              maxW="180px"
              placeholder="Signature name"
              display={{ base: 'none', md: 'block' }}
            />
          </HStack>
          <HStack spacing={1}>
            <Text fontSize="xs" color={mutedColor} mr={2}>
              {saveState === 'saving' ? 'Saving…' : 'Saved'}
            </Text>
            <Tooltip label="Undo">
              <IconButton size="sm" variant="ghost" icon={<FiCornerUpLeft />} aria-label="Undo" onClick={undo} isDisabled={!history.length} />
            </Tooltip>
            <Tooltip label="Redo">
              <IconButton size="sm" variant="ghost" icon={<FiCornerUpRight />} aria-label="Redo" onClick={redo} isDisabled={!future.length} />
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      {/* Body */}
      <Flex px={{ base: 3, md: 6 }} pt={5} align="flex-start" direction={{ base: 'column', xl: 'row' }}>
        {/* Left: editor */}
        <Box
          w={{ base: '100%', xl: '410px' }}
          flexShrink={0}
          mr={{ xl: 6 }}
          mb={{ base: 6, xl: 0 }}
          bg={panelBG}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          overflow="hidden"
        >
          <Tabs size="sm" colorScheme="purple" isLazy>
            <TabList px={2} pt={1}>
              <Tab>Design</Tab>
              <Tab>Images</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={3} py={3}>
                <BlockEditorPanel
                  spec={spec}
                  images={images}
                  selectedBlockId={selectedBlockId}
                  onSelectBlock={setSelectedBlockId}
                  onUpdateGlobal={handleUpdateGlobal}
                  onUpdateBlock={handleUpdateBlock}
                  onAddBlock={handleAddBlock}
                  onRemoveBlock={handleRemoveBlock}
                  onMoveBlock={handleMoveBlock}
                  onUpdateColumn={handleUpdateColumn}
                  onAddRow={handleAddRow}
                  onRemoveRow={handleRemoveRow}
                  onApplyTemplate={applyTemplate}
                />
              </TabPanel>
              <TabPanel px={3} py={3}>
                <ImageLibrary
                  images={images}
                  onImagesChange={persistImages}
                  onUseImage={(img) => {
                    handleAddBlock(null, 'image', {
                      src: img.url,
                      alt: img.name,
                      width: Math.min(img.width || 120, 200),
                      height: Math.round(Math.min(img.width || 120, 200) * ((img.height || 120) / (img.width || 120))),
                    });
                  }}
                />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>

        {/* Right: preview + install */}
        <VStack flex={1} minW={0} w="100%" align="stretch" spacing={5}>
          <SignaturePreview
            spec={spec}
            stats={stats}
            agentTouchedIds={agentTouchedIds}
            selectedBlockId={selectedBlockId}
            onSelectBlock={setSelectedBlockId}
          />
          <InstallPanel spec={spec} stats={stats} />
        </VStack>
      </Flex>

      {/* Template picker */}
      <Modal isOpen={templatePicker.isOpen} onClose={templatePicker.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Start from a template</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {TEMPLATES.map((t) => (
                <Box
                  key={t.id}
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={3}
                  cursor="pointer"
                  _hover={{ borderColor: 'purple.400', shadow: 'sm' }}
                  onClick={() => createSignature(t.id)}
                >
                  <Text fontWeight="bold" fontSize="sm">{t.name}</Text>
                  <Text fontSize="xs" color={mutedColor} mt={1}>{t.description}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </ModalBody>
          <ModalFooter justifyContent="space-between">
            <Text fontSize="xs" color={mutedColor}>
              Templates prefill from your Hub profile (name, title, NMLS, phone, email).
            </Text>
            <Button
              size="xs"
              variant="link"
              colorScheme="purple"
              leftIcon={<FiDownloadCloud />}
              onClick={() => { templatePicker.onClose(); importModal.onOpen(); }}
            >
              Or import your current Gmail signature
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Import current Gmail signature */}
      <ImportSignatureModal
        isOpen={importModal.isOpen}
        onClose={importModal.onClose}
        onImport={importAsNewSignature}
      />
    </Box>
  );
}
